import { fetcher, apiClient } from './apiClient';
import { CLOUD_UI_PLUGIN_ID } from '../components/deviceConfig/componentRegistry';

export type QrCodeConfig = {
    x: string;
    y: string;
    size: string;
    marginSize: number;
    target: string;
    utmParams: Record<string, string>;
};

export type PanelContentWithDuration = {
    /**
     * The name of a React component to render
     * i.e. 'UpcomingGames' | 'DraftKingsLogoBanner' | etc
     */
    component: string;

    /**
     * The props to pass to the React component. Valid values will vary depending on the Component.
     */
    params: Record<string, any> | null;

    /**
     * The number of seconds to display the attached content.
     * Setting this to 0 means that the content will be displayed indefinitely.
     * (This typically only makes sense to use when there's only a single item in the carousel array)
     */
    durationSeconds: number;

    /**
     * Optional QR code overlay for this content item.
     */
    qrCode?: QrCodeConfig;
};

export type CarouselConfig = PanelContentWithDuration[];

export type PanelConfig = {
    /**
     * commercialTakeover: true means that the commercialContent will be displayed immediately whenever the "imageRoute" is "commercial".
     * Setting this to false disables the commercialContent altogether (meaning that the regular "content" carousel will continue
     * to be displayed even when the "imageRoute" is "commercial")
     *
     * Default: false
     */
    commercialTakeover: boolean;

    /**
     * An array of content to display when "isCommercial" is "commercial"
     */
    commercialContent: CarouselConfig;

    /**
     * An array of content to display when "isCommercial" is false, or when "commercialTakeover" is false
     */
    content: CarouselConfig;
};

/**
 * Stored device configuration (Sulaco) is a superset: panels, isDebug, and iframeUri may all exist in the JSON.
 * The on-prem device only consumes the subset relevant to the active pluginId (embedded vs cloud-ui-v1).
 */
export type DeviceConfig = {
    /**
     * i.e. 'draft-kings-v1' | 'fanduel-v1' | 'cloud-ui-v1' | etc
     */
    pluginId: string;
    isDebug: boolean | null;

    topPanel: PanelConfig | null;
    middlePanel: PanelConfig | null;
    bottomPanel: PanelConfig | null;

    /**
     * HTTP or HTTPS URL for the cloud-ui-v1 plugin iframe. Ignored at runtime when another plugin is active.
     */
    iframeUri?: string | null;
};

/**
 * The shape of the API response
 */
export type DeviceConfigResponsePayload = {
    id: string;
    deviceId: string;
    config: DeviceConfig;
    createdAt: string;
    updatedAt: string;
};

const deviceConfigFetcher = async (url: string): Promise<DeviceConfigResponsePayload> => {
    const response = await fetcher<DeviceConfigResponsePayload>(url);

    return {
        id: response.id,
        deviceId: response.deviceId,
        config: response.config,
        createdAt: response.createdAt,
        updatedAt: response.updatedAt,
    } as DeviceConfigResponsePayload;
};

const nullParamsToArray = (_key: string, value: any) => (_key === 'params' && value === null ? [] : value);

/** Valid http(s) URL for iframe preview / cloud plugin (matches backend post-merge check). */
export function isValidIframeUri(url: string): boolean {
    const t = url.trim();
    if (!t || !/^https?:\/\//i.test(t)) {
        return false;
    }
    try {
        const parsed = new URL(t);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
        return false;
    }
}

/**
 * Request body for a visual-editor save: sparse patch so omitted keys (e.g. iframeUri on embedded) stay server-side.
 */
export function buildSparseVisualConfigPatch(config: DeviceConfig): Record<string, unknown> {
    if (config.pluginId === CLOUD_UI_PLUGIN_ID) {
        return {
            pluginId: config.pluginId,
            iframeUri: config.iframeUri ?? '',
        };
    }
    return {
        pluginId: config.pluginId,
        isDebug: config.isDebug,
        topPanel: config.topPanel,
        middlePanel: config.middlePanel,
        bottomPanel: config.bottomPanel,
    };
}

const updateDeviceConfigByDeviceId = async (
    csrfToken: string | null,
    deviceId: string,
    payload: Record<string, unknown>,
    options?: { replace?: boolean }
): Promise<null> => {
    if (csrfToken === null) {
        return null;
    }

    const url = `api/device/${deviceId}/config`;
    const method = options?.replace === true ? 'post' : 'patch';

    const response = await apiClient.request<null>({
        url,
        method,
        data: JSON.stringify(payload, nullParamsToArray),
        headers: {
            'Content-Type': 'application/json',
            'X-XSRF-TOKEN': csrfToken,
        },
    });

    return response.data;
};

const updateDeviceConfigByDeviceTagId = async (
    csrfToken: string | null,
    deviceTagId: string,
    payload: Record<string, unknown>
): Promise<null> => {
    if (csrfToken === null) {
        return null;
    }

    const response = await apiClient.put<null>(
        `api/device-tag/${deviceTagId}/config`,
        JSON.stringify(payload, nullParamsToArray),
        {
            headers: {
                'Content-Type': 'application/json',
                'X-XSRF-TOKEN': csrfToken,
            },
        }
    );

    return response.data;
};

export { deviceConfigFetcher, updateDeviceConfigByDeviceId, updateDeviceConfigByDeviceTagId };
